import {assertAndContinue} from 'Asserts';
import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ColumnDefinition,
  ColumnGroup,
  getColumnTypeFromColumnOrDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';

import {getSpreadsheetTypeForBasesColumn} from '../bases-columns/BasesColumnConstants';
import {
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableKind,
} from '../StructuredTableConstants';
import {ColumnDefinitionForSpreadsheetImport} from './ColumnDefinitionForSpreadsheetImport';

export function getSpreadsheetTypeForColumn(
  schemaType: string | null,
  structuredTableColumnOrColumnDefinition: StructuredTableColumn | ColumnDefinition,
  tableKind?: StructuredTableKind,
  columnGroup?: ColumnGroup | null
): SpreadsheetBulkImportConstants.COLUMN_TYPE | null {
  const structuredTableColumnType = getColumnTypeFromColumnOrDefinition(
    structuredTableColumnOrColumnDefinition
  );
  switch (structuredTableColumnType) {
    case TableColumnType.BATCH:
      return null;

    case TableColumnType.BIOENTITY_NAME:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.FILENAME;

    case TableColumnType.CONCENTRATION:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_CONCENTRATION_VALUE;

    case TableColumnType.CONTAINERS:
      if (tableKind === StructuredTableKind.LOOKUP) {
        return SpreadsheetBulkImportConstants.COLUMN_TYPE.STORABLE_NAME;
      } else {
        return SpreadsheetBulkImportConstants.COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE;
      }

    case TableColumnType.DEDICATED_LOOKUP:
      // Lookup columns are read-only and so they shouldn't be a supported target for spreadsheet import
      return null;

    case TableColumnType.DNA_BASES:
    case TableColumnType.RNA_BASES:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.RESIDUES;
    case TableColumnType.DNA_HELM:
    case TableColumnType.RNA_HELM:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.HELM;
    case TableColumnType.DNA_IDT:
    case TableColumnType.RNA_IDT:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.IDT;
    case TableColumnType.BASES:
      return getSpreadsheetTypeForBasesColumn(structuredTableColumnOrColumnDefinition);

    case TableColumnType.SEQUENCE_TOPOLOGY:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.SEQUENCE_TOPOLOGY;

    case TableColumnType.SMILES:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.SMILES;

    case TableColumnType.EXISTING_BIOENTITY:
      switch (schemaType) {
        case ModelTypeConstants.RESULT_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.FILENAME; // matches legacy DataTable
        default:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY;
      }

    case TableColumnType.MISSING_FIELD:
      return null;

    case TableColumnType.NUMBER_OF_CONTAINERS:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.NUMBER_OF_CONTAINERS;

    case TableColumnType.PARENT_STORABLE:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.PARENT_STORABLE_BARCODE;

    case TableColumnType.PLATE:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.STORABLE_NAME;

    case TableColumnType.BOX:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.STORABLE_NAME;

    case TableColumnType.POSITION:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES;

    case TableColumnType.ALIAS:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.ALIAS;

    case TableColumnType.STRING:
      if (schemaType === ModelTypeConstants.PLATE_SCHEMA || schemaType === ModelTypeConstants.BOX_SCHEMA) {
        return SpreadsheetBulkImportConstants.COLUMN_TYPE.BARCODE;
      } else {
        assertAndContinue(
          false,
          `Unsupported schema type ${schemaType} for column type ${structuredTableColumnType}`
        );
        return null;
      }

    case TableColumnType.TAG_VALUE: {
      if (schemaType == null) {
        assertAndContinue(false, 'Unexpected tag value column with null schemaType');
        return null;
      }

      switch (schemaType) {
        case ModelTypeConstants.TAG_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.SCHEMA_FIELD;
        case ModelTypeConstants.CONTAINER_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.CONTAINER_SCHEMA_FIELD;
        case ModelTypeConstants.RESULT_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.RESULT_SCHEMA_FIELD;
        case ModelTypeConstants.PROCESS_TASK_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD;
        case ModelTypeConstants.PROCESS_OUTPUT_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD;
        case ModelTypeConstants.PLATE_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.PLATE_SCHEMA_FIELD;
        case ModelTypeConstants.BOX_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.BOX_SCHEMA_FIELD;
        default:
          assertAndContinue(false, `Unsupported schema type ${schemaType}`);
          return null;
      }
    }

    case TableColumnType.QUANTITY:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.TRANSFER_QUANTITY;

    case TableColumnType.STAGE_RUN:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.STAGE_RUN_NAME;

    case TableColumnType.UNIT_SELECTOR:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.UNIT_SELECTOR;

    case TableColumnType.PARTIES:
      return columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS
        ? SpreadsheetBulkImportConstants.COLUMN_TYPE.SAMPLE_OWNERS
        : columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS
          ? SpreadsheetBulkImportConstants.COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS
          : null;

    case TableColumnType.SAMPLE_RESTRICTION_STATUS:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS;

    case TableColumnType.PROCESS_TASK:
      switch (schemaType) {
        case ModelTypeConstants.PROCESS_OUTPUT_SCHEMA:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_TASK;
        default:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD;
      }

    case TableColumnType.PROCESS_OUTPUT:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD;

    case TableColumnType.PROCESS_TASK_ASSIGNEE:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_TASK_ASSIGNEE;

    case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
      // TODO(BNCH-29207): evaluate how scheduled on date should be treated in spreadsheet import
      return null;

    case TableColumnType.PROCESS_TASK_STATUS:
      // Task status is not editable upon creation and so it shouldn't be a supported target
      // for spreadsheet import.
      return null;

    case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
      // Task execution origin is not editable so it shouldn't be a supported target
      // for spreadsheet import.
      return null;

    case TableColumnType.CUSTOM:
      // TODO: BNCH-3800
      return null;
    case TableColumnType.STUDIES:
      return null;
    case TableColumnType.PROCEDURE_CONDITION_REPLICATES:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCEDURE_CONDITION_REPLICATES;
    case TableColumnType.COMPLEX_POLYMER_COMPONENT:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT;
    case TableColumnType.COMPLEX_POLYMER_CONNECTIONS:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.COMPLEX_POLYMER_CONNECTION;
    case TableColumnType.OLIGO_DUPLEX_OFFSET:
      return SpreadsheetBulkImportConstants.COLUMN_TYPE.OLIGO_DUPLEX_OFFSET;
    case TableColumnType.ANTIBODY_DOMAIN:
      // Antibody csv import is unsupported for now
      return null;
    case TableColumnType.EXISTING_BIOENTITY_REFERENCE:
      // Import of reference columns (e.g. an ADC's Protein / Linker-payload) is unsupported for now; they
      // must not import as the registered entity.
      return null;
    case TableColumnType.ADC_CONJUGATION_SITES:
      // Sites are chosen from the row's candidates, which import cannot resolve yet.
      return null;
    case TableColumnType.ADC_LINKER_PAYLOAD_R_GROUP:
      // The R-group is chosen from the row's linker-payload, which import cannot resolve yet.
      return null;
    default:
      assertAndContinue(false, `Unsupported column type ${structuredTableColumnType}`);
      return null;
  }
}

export default function getSpreadsheetColumnTypes(
  columnDefinitionsForSpreadsheetImport: Array<ColumnDefinitionForSpreadsheetImport>
): Array<SpreadsheetBulkImportConstants.COLUMN_TYPE> {
  const spreadsheetColumnTypes: Array<SpreadsheetBulkImportConstants.COLUMN_TYPE> = [
    // The user always has the option to ignore a column
    SpreadsheetBulkImportConstants.COLUMN_TYPE.IGNORE,
  ];

  for (const {columnDefinition, spreadsheetColumnType} of columnDefinitionsForSpreadsheetImport) {
    if (columnDefinition.locked) {
      // Can't import into read-only columns
      continue;
    }
    if (spreadsheetColumnType != null) {
      spreadsheetColumnTypes.push(spreadsheetColumnType);
    }
  }

  // Dedupe. There should only be one element of each type.
  // In particular, we need to dedupe COLUMN_TYPE.SCHEMA_FIELD,
  // because there may be multiple structured table columns that are for schema fields.
  return [...new Set(spreadsheetColumnTypes)];
}
